<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  MEDIA_KINDS,
  REEL_RATIO,
  facebookRatio,
  fillsInstagramFrame,
  INSTAGRAM_FRAME_RATIO,
  instagramCarouselRatio,
  instagramRatio,
  isCropped,
} from '../../../ochat/marketing/previewFrame';

// Muestra lo que sale publicado, no una tarjeta genérica: la forma real de cada
// pieza en cada red. Antes la imagen iba recortada a 256 px de alto y un cliente
// no podía ver cómo terminaba quedando su posteo.
const props = defineProps({
  mediaUrls: { type: Array, default: () => [] },
  // { width, height } por archivo, en el mismo orden; null si es un video o si
  // todavía no se leyó.
  mediaSizes: { type: Array, default: () => [] },
  mediaKind: { type: String, default: null },
  coverUrl: { type: String, default: '' },
  fill: { type: Boolean, default: false },
  platforms: { type: Array, default: () => [] },
  caption: { type: String, default: '' },
  link: { type: String, default: '' },
  postTargets: { type: Array, default: () => [] },
  whatsappEnabled: { type: Boolean, default: false },
  whatsappBody: { type: String, default: '' },
  whatsappHasImageHeader: { type: Boolean, default: false },
  whatsappImageUrl: { type: String, default: '' },
});

const { t } = useI18n();

const PLATFORM_ORDER = ['instagram', 'facebook'];

const postText = computed(() =>
  [props.caption, props.link].filter(Boolean).join('\n\n')
);

const hasPost = computed(() => props.postTargets.length > 0);
const isReel = computed(() => props.mediaKind === MEDIA_KINDS.REEL);

// Instagram primero: es la red con más reglas y la que más sorprende.
const orderedPlatforms = computed(() =>
  PLATFORM_ORDER.filter(platform => props.platforms.includes(platform))
);
const activePlatform = ref('instagram');
watch(
  orderedPlatforms,
  platforms => {
    if (!platforms.includes(activePlatform.value)) {
      [activePlatform.value] = platforms;
    }
  },
  { immediate: true }
);
const isInstagram = computed(() => activePlatform.value === 'instagram');

const slideIndex = ref(0);
watch(
  () => props.mediaUrls,
  () => {
    slideIndex.value = 0;
  }
);
const currentUrl = computed(() => props.mediaUrls[slideIndex.value] ?? '');
const hasSeveral = computed(() => props.mediaUrls.length > 1);

const naturalRatios = computed(() =>
  props.mediaSizes.map(size =>
    size && size.height ? size.width / size.height : null
  )
);

const currentNaturalRatio = computed(
  () => naturalRatios.value[slideIndex.value]
);

// La misma cuenta que hace el servidor al preparar cada copia: lo que se dibuja
// acá es lo que se publica. Instagram y Facebook tienen copias distintas, así que
// recortar para Instagram no toca lo que sale en Facebook.
const finalRatio = computed(() => {
  if (isReel.value) return REEL_RATIO;
  if (!isInstagram.value) return facebookRatio(currentNaturalRatio.value);

  const options = { fill: props.fill };
  if (props.mediaKind === MEDIA_KINDS.CAROUSEL) {
    return instagramCarouselRatio(naturalRatios.value, options);
  }
  return instagramRatio(naturalRatios.value[0], options);
});

// El cuadro de Instagram es siempre 4:5; el de Facebook es la imagen misma.
const frameRatio = computed(() => {
  if (isReel.value) return REEL_RATIO;
  return isInstagram.value ? INSTAGRAM_FRAME_RATIO : finalRatio.value;
});

// Es lo que vio el cliente sin querer: Instagram rellena con la misma imagen
// desenfocada el espacio que la imagen no ocupa. Mostrarlo acá evita la sorpresa.
const showsBlurredFill = computed(
  () =>
    !isReel.value && isInstagram.value && !fillsInstagramFrame(finalRatio.value)
);

const showsCropNotice = computed(
  () =>
    !isReel.value &&
    isInstagram.value &&
    isCropped(currentNaturalRatio.value, finalRatio.value)
);

const RATIO_NAMES = [
  [4 / 5, '4:5'],
  [1, '1:1'],
  [1.91, '1.91:1'],
];
const ratioLabel = computed(() => {
  const named = RATIO_NAMES.find(
    ([value]) => Math.abs(value - finalRatio.value) < 0.01
  );
  return named ? named[1] : `${finalRatio.value.toFixed(2)}:1`;
});

const showPrevious = () => {
  slideIndex.value =
    (slideIndex.value - 1 + props.mediaUrls.length) % props.mediaUrls.length;
};
const showNext = () => {
  slideIndex.value = (slideIndex.value + 1) % props.mediaUrls.length;
};
</script>

<template>
  <aside class="flex flex-col gap-4">
    <span class="text-sm font-medium text-n-slate-12">
      {{ t('MARKETING.CAMPAIGN.PREVIEW.TITLE') }}
    </span>

    <!-- Posteo -->
    <div
      v-if="hasPost"
      class="flex flex-col overflow-hidden rounded-xl border border-n-weak bg-n-solid-2"
    >
      <div class="flex items-center gap-2 px-4 py-3 border-b border-n-weak">
        <span class="i-lucide-image size-4 text-n-slate-11" />
        <span class="text-xs font-medium text-n-slate-12">
          {{ t('MARKETING.CAMPAIGN.PREVIEW.POST') }}
        </span>
        <span class="text-xs text-n-slate-11 truncate">
          {{ postTargets.join(' · ') }}
        </span>
      </div>

      <!-- Una pestaña por red: no salen igual -->
      <div v-if="orderedPlatforms.length > 1" class="flex gap-1 px-3 pt-3">
        <button
          v-for="platform in orderedPlatforms"
          :key="platform"
          type="button"
          class="px-3 py-1 text-xs rounded-lg"
          :class="
            activePlatform === platform
              ? 'bg-n-alpha-2 text-n-slate-12'
              : 'text-n-slate-11'
          "
          @click="activePlatform = platform"
        >
          {{
            platform === 'instagram'
              ? t('MARKETING.CAMPAIGN.PREVIEW.INSTAGRAM')
              : t('MARKETING.CAMPAIGN.PREVIEW.FACEBOOK')
          }}
        </button>
      </div>

      <div v-if="currentUrl" class="flex flex-col gap-2 p-3">
        <div
          class="relative w-full overflow-hidden rounded-lg bg-n-alpha-black2"
          :class="{ 'max-w-[16rem] mx-auto': isReel }"
          :style="{ aspectRatio: frameRatio }"
        >
          <video
            v-if="isReel"
            :key="currentUrl"
            :src="currentUrl"
            :poster="coverUrl || undefined"
            class="absolute inset-0 object-cover w-full h-full"
            controls
            muted
            playsinline
            preload="metadata"
          />
          <template v-else>
            <img
              v-if="showsBlurredFill"
              :src="currentUrl"
              alt=""
              class="absolute inset-0 object-cover w-full h-full scale-110 blur-xl opacity-70"
            />
            <img
              :src="currentUrl"
              alt=""
              class="absolute inset-x-0 w-full -translate-y-1/2 top-1/2 object-cover"
              :style="{ aspectRatio: finalRatio }"
            />
          </template>

          <template v-if="hasSeveral">
            <button
              type="button"
              class="absolute flex items-center justify-center -translate-y-1/2 rounded-full top-1/2 ltr:left-2 rtl:right-2 size-7 bg-n-alpha-black1 text-n-slate-12"
              :title="t('MARKETING.CAMPAIGN.PREVIEW.PREVIOUS')"
              @click="showPrevious"
            >
              <span class="i-lucide-chevron-left size-4" />
            </button>
            <button
              type="button"
              class="absolute flex items-center justify-center -translate-y-1/2 rounded-full top-1/2 ltr:right-2 rtl:left-2 size-7 bg-n-alpha-black1 text-n-slate-12"
              :title="t('MARKETING.CAMPAIGN.PREVIEW.NEXT')"
              @click="showNext"
            >
              <span class="i-lucide-chevron-right size-4" />
            </button>
          </template>
        </div>

        <div v-if="hasSeveral" class="flex justify-center gap-1">
          <span
            v-for="(url, index) in mediaUrls"
            :key="url"
            class="rounded-full size-1.5"
            :class="index === slideIndex ? 'bg-n-brand' : 'bg-n-slate-6'"
          />
        </div>

        <p v-if="showsBlurredFill" class="text-xs text-n-amber-11">
          {{ t('MARKETING.CAMPAIGN.PREVIEW.BLURRED_FILL') }}
        </p>
        <p v-else-if="showsCropNotice" class="text-xs text-n-slate-11">
          {{ t('MARKETING.CAMPAIGN.PREVIEW.CROPPED', { ratio: ratioLabel }) }}
        </p>
        <p v-if="isReel && !isInstagram" class="text-xs text-n-slate-11">
          {{ t('MARKETING.CAMPAIGN.PREVIEW.FACEBOOK_REEL') }}
        </p>
      </div>
      <div
        v-else
        class="flex items-center justify-center h-40 text-xs text-n-slate-11"
      >
        {{ t('MARKETING.CAMPAIGN.PREVIEW.NO_IMAGE') }}
      </div>

      <p
        v-if="postText"
        class="px-4 pb-3 text-sm whitespace-pre-line text-n-slate-12"
      >
        {{ postText }}
      </p>
    </div>

    <!-- WhatsApp -->
    <div
      v-if="whatsappEnabled"
      class="flex flex-col gap-2 p-4 rounded-xl border border-n-weak bg-n-solid-2"
    >
      <div class="flex items-center gap-2">
        <span class="i-lucide-message-circle size-4 text-n-teal-11" />
        <span class="text-xs font-medium text-n-slate-12">
          {{ t('MARKETING.CAMPAIGN.PREVIEW.WHATSAPP') }}
        </span>
      </div>
      <!-- La burbuja imita el mensaje real: imagen de encabezado arriba y el
           cuerpo de la plantilla con las variables ya reemplazadas. La imagen va
           entera: WhatsApp no recorta el encabezado. -->
      <div class="overflow-hidden rounded-lg bg-n-alpha-2 max-w-[20rem]">
        <img
          v-if="whatsappHasImageHeader && whatsappImageUrl"
          :src="whatsappImageUrl"
          alt=""
          class="object-contain w-full"
        />
        <p
          v-if="whatsappBody"
          class="px-3 py-2 text-sm whitespace-pre-line text-n-slate-12"
        >
          {{ whatsappBody }}
        </p>
        <p v-else class="px-3 py-2 text-xs text-n-slate-11">
          {{ t('MARKETING.CAMPAIGN.PREVIEW.PICK_TEMPLATE') }}
        </p>
      </div>
    </div>

    <p v-if="!hasPost && !whatsappEnabled" class="text-xs text-n-slate-11">
      {{ t('MARKETING.CAMPAIGN.PREVIEW.EMPTY') }}
    </p>
  </aside>
</template>
